import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Search, Send, Users } from 'lucide-react';
import { Avatar } from '../Avatar';
import { useLoadingState } from '../../hooks/useLoadingState';
import { LoadingPanel } from '../brand/TwindenLoader';
import {
  api,
  ApiError,
  type FamilyRole,
  type PersonYouKnow,
} from '../../lib/api';
import { useT, type MessageKey } from '../../i18n';

const ROLE_LABEL: Record<FamilyRole, MessageKey> = {
  root: 'account.role.root',
  admin: 'account.role.admin',
  member: 'account.role.member',
};

const fold = (text: string) =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase();

/**
 * The picker's search, on the device (D350): by name (any part, accents
 * ignored) or by handle, with or without the @.
 */
export function filterPeople(
  people: PersonYouKnow[],
  query: string
): PersonYouKnow[] {
  const q = fold(query.trim());
  if (!q) return people;
  const handleQuery = q.replace(/^@/, '');
  return people.filter(
    (person) =>
      fold(person.displayName).includes(q) ||
      (handleQuery !== '' && person.handle.includes(handleQuery))
  );
}

interface AddSomeoneYouKnowProps {
  familyId: string;
  /** Only roots offer admin or root (D1); the server checks it too. */
  canGrantRoot: boolean;
  /** After an invitation goes out: the page's Pending list reloads. */
  onInvited?: () => void;
}

// "Add someone you know" (D350): roots and admins invite someone who is in
// another of their families. They get an invitation and join only if they
// say yes (D351). The server decides who may be listed and invited.
export function AddSomeoneYouKnow({
  familyId,
  canGrantRoot,
  onInvited,
}: AddSomeoneYouKnowProps) {
  const t = useT();
  const toast = useToast();
  const [people, setPeople] = useState<PersonYouKnow[] | null>(null);
  const [failed, setFailed] = useState<unknown>(null);
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<PersonYouKnow | null>(null);
  const [role, setRole] = useState<FamilyRole>('member');
  const [sending, setSending] = useState(false);

  // useT and useToast hand out new functions on every render.
  const say = useRef({ t, toast });
  say.current = { t, toast };

  const load = useCallback(async () => {
    setFailed(null);
    try {
      const response = await api.getPeopleYouKnow(familyId);
      setPeople(response.data.people);
    } catch (err) {
      setFailed(err);
    }
  }, [familyId]);

  useEffect(() => {
    setPeople(null);
    setChosen(null);
    void load();
  }, [load]);

  // The small mark while the list loads, Try again if it fails (D248–D250).
  const listState = useLoadingState({
    loading: people === null && !failed,
    hasData: people !== null,
    error: failed,
    retry: () => void load(),
  });

  const shown = useMemo(
    () => (people ? filterPeople(people, query) : []),
    [people, query]
  );

  const roleChoices: FamilyRole[] = canGrantRoot
    ? ['member', 'admin', 'root']
    : ['member'];

  const send = async () => {
    if (!chosen) return;
    setSending(true);
    try {
      await api.createFamilyInvite(familyId, {
        userId: chosen.id,
        familyRole: role,
      });
      toast({
        title: t('account.knows.sent', { name: chosen.displayName }),
        description: t('account.knows.sentHint'),
        status: 'success',
      });
      // They're waiting now: off the list (D350).
      setPeople(
        (current) => current?.filter((p) => p.id !== chosen.id) ?? null
      );
      setChosen(null);
      setRole('member');
      onInvited?.();
    } catch (err) {
      toast({
        title: t('account.knows.failed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
      // Someone else may have invited them, or they joined meanwhile.
      void load();
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      bg="surface"
      p={5}
      borderRadius="card"
      borderWidth="1px"
      borderColor="line"
    >
      <VStack align="stretch" spacing={4}>
        <VStack align="start" spacing={0}>
          <Heading size="sm">{t('account.knows.title')}</Heading>
          <Text fontSize="sm" color="muted">
            {t('account.knows.hint')}
          </Text>
        </VStack>

        {chosen ? (
          <VStack align="stretch" spacing={4} data-testid="knows-chosen">
            <HStack spacing={3}>
              <Avatar
                name={chosen.displayName}
                src={chosen.avatarUrl ?? undefined}
                ownerId={chosen.id}
                familyId={chosen.viaFamilyId}
                size="sm"
              />
              <Box minW={0}>
                <Text fontWeight={700} noOfLines={1}>
                  {t('account.knows.inviting', { name: chosen.displayName })}
                </Text>
                <Text fontSize="sm" color="muted" noOfLines={1}>
                  @{chosen.handle}
                </Text>
              </Box>
            </HStack>
            {roleChoices.length > 1 && (
              <FormControl>
                <FormLabel fontSize="sm">{t('account.knows.role')}</FormLabel>
                <Select
                  value={role}
                  onChange={(event) =>
                    setRole(event.target.value as FamilyRole)
                  }
                >
                  {roleChoices.map((choice) => (
                    <option key={choice} value={choice}>
                      {t(ROLE_LABEL[choice])}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
            <HStack spacing={2} flexWrap="wrap">
              <Button
                leftIcon={<Send size={16} />}
                isLoading={sending}
                onClick={() => void send()}
              >
                {t('account.knows.send')}
              </Button>
              <Button
                variant="ghost"
                colorScheme="gray"
                isDisabled={sending}
                onClick={() => {
                  setChosen(null);
                  setRole('member');
                }}
              >
                {t('account.knows.cancel')}
              </Button>
            </HStack>
          </VStack>
        ) : listState.show || people === null ? (
          <LoadingPanel state={listState} size="inline" />
        ) : people.length === 0 ? (
          <VStack py={4} spacing={2} color="muted" textAlign="center">
            <Users size={24} aria-hidden="true" />
            <Text fontSize="15px">{t('account.knows.empty')}</Text>
          </VStack>
        ) : (
          <VStack align="stretch" spacing={3}>
            <InputGroup>
              <InputLeftElement pointerEvents="none" color="muted">
                <Search size={16} />
              </InputLeftElement>
              <Input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('account.knows.search')}
                aria-label={t('account.knows.search')}
              />
            </InputGroup>
            {shown.length === 0 ? (
              <Text fontSize="sm" color="muted" py={2}>
                {t('account.knows.noMatch', { query: query.trim() })}
              </Text>
            ) : (
              <VStack
                as="ul"
                align="stretch"
                spacing={1}
                listStyleType="none"
                maxH="320px"
                overflowY="auto"
              >
                {shown.map((person) => (
                  <Box as="li" key={person.id}>
                    <HStack
                      as="button"
                      type="button"
                      w="full"
                      spacing={3}
                      p={2}
                      borderRadius="md"
                      textAlign="left"
                      _hover={{ bg: 'surface.subtle' }}
                      aria-label={t('account.knows.pick', {
                        name: person.displayName,
                      })}
                      onClick={() => setChosen(person)}
                    >
                      <Avatar
                        name={person.displayName}
                        src={person.avatarUrl ?? undefined}
                        ownerId={person.id}
                        familyId={person.viaFamilyId}
                        size="sm"
                      />
                      <Box minW={0}>
                        <Text fontWeight="medium" noOfLines={1}>
                          {person.displayName}
                        </Text>
                        <Text fontSize="sm" color="muted" noOfLines={1}>
                          @{person.handle}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
}
